import { type ReactNode, useState } from "react";
import type { Item } from "../api/types";
import { useT } from "../i18n";
import { childrenOf } from "../lib/tasks";
import { useSyncState } from "../store";
import type { QuickDefaults } from "../sync/quickSubmit";
import { IconPlus } from "../ui/icons";
import { TaskEditor } from "./TaskEditor";
import { TaskRow, type TaskRowProps } from "./TaskRow";

/** A task and (unless collapsed) its sub-tasks, indented. */
export function TaskTree({ item, showCompleted, ...rowProps }: TaskRowProps & { showCompleted?: boolean }) {
  const state = useSyncState();
  const children = childrenOf(state, item.id, showCompleted);
  return (
    <>
      <TaskRow item={item} hasChildren={children.length > 0} {...rowProps} />
      {children.length > 0 && !item.is_collapsed && (
        <div className="subtasks">
          {children.map((c) => (
            <TaskTree
              key={c.id}
              item={c}
              showCompleted={showCompleted}
              showProject={false}
              hideDate={rowProps.hideDate}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** "+ Add task" that turns into an inline editor. */
export function AddTask({ defaults, label }: { defaults: QuickDefaults; label?: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (open) return <TaskEditor defaults={defaults} onClose={() => setOpen(false)} />;
  return (
    <button type="button" className="add-task-btn" onClick={() => setOpen(true)} data-testid="add-task-inline">
      <span className="plus">
        <IconPlus size={16} />
      </span>
      {label ?? t("addTask")}
    </button>
  );
}

export function EmptyState({ text }: { text: string }) {
  return (
    <div className="empty-state">
      <svg
        width="120"
        height="90"
        viewBox="0 0 120 90"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden="true"
      >
        <rect x="20" y="14" width="80" height="62" rx="8" />
        <path d="M36 36l6 6 12-12M62 36h24M36 56h50" strokeLinecap="round" />
      </svg>
      <div>{text}</div>
    </div>
  );
}

export function flatTasks(list: Item[]): Item[] {
  return list;
}
